import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserRoundPen } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Section } from "@/components/CaseBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "rounded-none focus:ring-2 focus:ring-blue-800";
const STATUS = { pending: "Awaiting acceptance", accepted: "Completed", cancelled: "Withdrawn" };

export const ChangeAdvocate = ({ kase, caseId, onDone }) => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/cases/${caseId}/advocate-changes`).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = rows.find((r) => r.status === "pending");
  const current = kase.my_side === "respondent" ? kase.respondent_advocate : kase.claimant_advocate;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/advocate-change`, form);
      toast.success("Brief offered to the new advocate; your present counsel has been informed");
      setForm({ name: "", email: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    try {
      await api.post(`/advocate-changes/${id}/cancel`);
      toast.success("Request withdrawn");
      load(); onDone?.();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Section title="Change of advocate" testId="change-advocate-section">
      <div className="px-6 py-5 space-y-5 text-sm">
        <p className="text-muted-foreground">
          Present counsel: <strong data-testid="change-advocate-current">{current?.name || "—"}</strong>.
          Enter the new advocate's details; they receive an invitation to accept the brief and your present
          counsel is asked to acknowledge exit. Past filings remain on record.
        </p>
        {pending ? (
          <div className="border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-4" data-testid="change-advocate-pending">
            <p className="label-caps">Awaiting acceptance</p>
            <p className="mt-1">{pending.new_name} ({pending.new_email}) was offered the brief on {fmtDateTime(pending.created_at)}.
              {pending.old_exited_at ? " Present counsel has acknowledged exit." : " Present counsel has not yet acknowledged."}</p>
            <Button variant="outline" size="sm" className="rounded-none mt-3 uppercase tracking-widest text-xs"
              data-testid="change-advocate-cancel" onClick={() => cancel(pending.id)}>Withdraw request</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end" data-testid="change-advocate-form">
            <div className="space-y-2">
              <Label className="label-caps">New advocate's name</Label>
              <Input required data-testid="change-advocate-name" value={form.name} className={inputCls}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">New advocate's email</Label>
              <Input required type="email" data-testid="change-advocate-email" value={form.email} className={inputCls}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <Button type="submit" disabled={busy} data-testid="change-advocate-submit"
              className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11">
              <UserRoundPen className="h-4 w-4 mr-2" /> {busy ? "Sending…" : "Offer brief"}
            </Button>
          </form>
        )}
        {rows.filter((r) => r.status !== "pending").length > 0 && (
          <ul className="divide-y divide-border border border-border" data-testid="change-advocate-history">
            {rows.filter((r) => r.status !== "pending").map((r) => (
              <li key={r.id} className="px-4 py-2 flex justify-between gap-4 text-xs">
                <span>{r.old_advocate_name || "Former counsel"} → {r.new_name} ({r.new_email})</span>
                <span className="font-mono text-muted-foreground">{STATUS[r.status]} · {fmtDateTime(r.accepted_at || r.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
};
